import {
  Box,
  Button,
  Container,
  Fieldset,
  Flex,
  Image,
  Input,
  Text,
  IconButton
} from '@chakra-ui/react';
import logo from '@/assets/icon/logo.svg';
import { useForm } from 'react-hook-form';
import { fetchAPI } from '@/api/fetch';
import { apiResetPassword } from '@/api/services';
import InfoCard from '@/components/data-display/InfoCard';
import { InfoCardType } from '@/components/data-display/InfoCardTypes';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CaretLeft, Eye, EyeSlash } from '@phosphor-icons/react';

interface FormValues {
  password: string;
  confirmedPassword: string;
}

const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  maxLength: 30,
  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/
};

const calculatePasswordStrength = (password: string): number => {
  let strength = 0;

  // Length check
  if (password.length >= PASSWORD_REQUIREMENTS.minLength) strength += 20;
  if (password.length >= 12) strength += 10;

  // Character type checks
  if (/[A-Z]/.test(password)) strength += 20;
  if (/[a-z]/.test(password)) strength += 20;
  if (/[0-9]/.test(password)) strength += 20;
  if (/[@$!%*?&]/.test(password)) strength += 10;

  return Math.min(strength, 100);
};

const getPasswordStrengthColor = (strength: number): string => {
  if (strength <= 30) return 'error.700';
  if (strength <= 60) return 'warning.700';
  if (strength <= 80) return 'info.700';
  return 'success.700';
};

const getPasswordStrengthText = (strength: number): string => {
  if (strength <= 30) return 'Weak';
  if (strength <= 60) return 'Fair';
  if (strength <= 80) return 'Good';
  return 'Strong';
};

export default function ResetPassword() {
  const [resetSuccess, setResetSuccess] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors }
  } = useForm<FormValues>({
    shouldFocusError: false,
    mode: 'onChange',
    criteriaMode: 'all'
  });

  const password = watch('password');

  useEffect(() => {
    const token = searchParams.get('accessToken');
    if (!token) {
      navigate('/login');
      return;
    }
    setAccessToken(token);
  }, [searchParams, navigate]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPasswordStrength(calculatePasswordStrength(newPassword));
  };

  async function onSubmit(values: FormValues) {
    if (resetSuccess) {
      navigate('/login');
      return;
    }

    try {
      setIsLoading(true);
      setSubmitError(null);

      const response = await fetchAPI({
        request: () =>
          apiResetPassword(accessToken, {
            password: values.password
          })
      });

      if (response.success) {
        setResetSuccess(true);
      } else {
        setSubmitError(response.message || 'Failed to reset password. Please try again.');
        console.error(response.message);
      }
    } catch (error) {
      setSubmitError('An unexpected error occurred. Please try again.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Container
      maxW="100%"
      minH="100dvh"
      w="100%"
      h="100%"
      p={0}
      bg="background.primary"
      display="flex"
      flexDir="column"
      role="main"
      aria-label="Reset Password"
    >
      <header
        style={{
          position: 'fixed',
          top: 0,
          width: '100%',
          zIndex: 1000
        }}
      >
        <Flex p="32px 50px">
          <Image
            src={logo}
            alt="Polaris Jobs Logo"
            width="145px"
            onClick={() => navigate('/')}
            cursor="pointer"
            role="button"
            aria-label="Go to homepage"
          />
        </Flex>
      </header>
      <Flex flexGrow={1} mx="auto" overflowY="auto" pb="60px">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Fieldset.Root
            borderRadius="10px"
            w="450px"
            position="fixed"
            top="50%"
            left="50%"
            marginTop="-310px"
            marginLeft="-225px"
            flexDirection="column"
            role="form"
            aria-label="Reset password form"
          >
            <Box>
              <Flex justifyContent="left" alignItems="center">
                <Box
                  ml="-50px"
                  pr="24px"
                  onClick={() => navigate('/')}
                  cursor="pointer"
                  role="button"
                  aria-label="Go back"
                >
                  <CaretLeft size={24} color="neutrals.900" weight="bold" />
                </Box>
                <Text fontSize="36px" fontWeight="bold">
                  Reset password
                </Text>
              </Flex>
              {resetSuccess && (
                <InfoCard
                  my="16px"
                  type={InfoCardType.Success}
                  title="Password reset successful."
                  contents={
                    <Flex fontSize="14px" mt="5px" display="inline">
                      Your password has been successfully reset. Please use your new password to log
                      in to your account.
                    </Flex>
                  }
                />
              )}
              <Box mt="20px">
                <Text fontWeight={500} fontSize="16px" color="neutrals.900" mb="10px">
                  Password
                </Text>
                <Flex position="relative">
                  <Input
                    fontSize="16px"
                    height="48px"
                    borderRadius="8px"
                    id="new-password"
                    placeholder="Enter your new password"
                    _placeholder={{
                      color: 'neutrals.300'
                    }}
                    _focus={{
                      boxShadow: 'none',
                      outline: 'none'
                    }}
                    disabled={resetSuccess || isLoading}
                    borderColor={errors.password ? 'error.700' : 'neutrals.300'}
                    variant="outline"
                    type={showPassword ? 'text' : 'password'}
                    p="12px 16px"
                    h="50px"
                    _invalid={{
                      borderWidth: 1,
                      borderColor: 'error.700'
                    }}
                    aria-invalid={errors.password ? 'true' : 'false'}
                    aria-describedby="password-error password-requirements"
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: PASSWORD_REQUIREMENTS.minLength,
                        message: `Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters`
                      },
                      maxLength: {
                        value: PASSWORD_REQUIREMENTS.maxLength,
                        message: `Password must be at most ${PASSWORD_REQUIREMENTS.maxLength} characters`
                      },
                      pattern: {
                        value: PASSWORD_REQUIREMENTS.pattern,
                        message:
                          'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
                      },
                      onChange: handlePasswordChange
                    })}
                  />
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    position="absolute"
                    right="10px"
                    top="50%"
                    transform="translateY(-50%)"
                    variant="ghost"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </IconButton>
                </Flex>
                {password && (
                  <Box mt="8px">
                    <Box w="100%" h="4px" bg="neutrals.100" borderRadius="4px" overflow="hidden">
                      <Box
                        h="100%"
                        w={`${passwordStrength}%`}
                        bg={getPasswordStrengthColor(passwordStrength)}
                        transition="all 0.3s ease"
                      />
                    </Box>
                    <Text
                      fontSize="12px"
                      color={getPasswordStrengthColor(passwordStrength)}
                      mt="4px"
                    >
                      {getPasswordStrengthText(passwordStrength)}
                    </Text>
                  </Box>
                )}
                {errors.password && (
                  <Text id="password-error" fontSize="16px" color="error.700" mt="5px" ml="5px">
                    {errors.password.message}
                  </Text>
                )}
              </Box>
              <Box mt="20px">
                <Text fontWeight={500} fontSize="16px" color="neutrals.900" mb="10px">
                  Confirm new password
                </Text>
                <Flex position="relative">
                  <Input
                    fontSize="16px"
                    height="48px"
                    borderRadius="8px"
                    id="confirmed-password"
                    placeholder="Enter your new password again"
                    _placeholder={{
                      color: 'neutrals.300'
                    }}
                    _focus={{
                      boxShadow: 'none',
                      outline: 'none'
                    }}
                    disabled={resetSuccess || isLoading}
                    borderColor={errors.confirmedPassword ? 'error.700' : 'neutrals.300'}
                    variant="outline"
                    type={showConfirmPassword ? 'text' : 'password'}
                    p="12px 16px"
                    h="50px"
                    _invalid={{
                      borderWidth: 1,
                      borderColor: 'error.700'
                    }}
                    aria-invalid={errors.confirmedPassword ? 'true' : 'false'}
                    aria-describedby="confirm-password-error"
                    {...register('confirmedPassword', {
                      required: 'Please confirm your password',
                      validate: (value) => value === password || 'The passwords do not match'
                    })}
                  />
                  <IconButton
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    position="absolute"
                    right="10px"
                    top="50%"
                    transform="translateY(-50%)"
                    variant="ghost"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </IconButton>
                </Flex>
                {errors.confirmedPassword && (
                  <Text
                    id="confirm-password-error"
                    fontSize="16px"
                    color="error.700"
                    mt="5px"
                    ml="5px"
                  >
                    {errors.confirmedPassword.message}
                  </Text>
                )}
              </Box>
              {submitError && (
                <Text fontSize="16px" color="error.700" mt="5px" ml="5px">
                  {submitError}
                </Text>
              )}
            </Box>
            <Box mt="20px" display="flex" justifyContent="center">
              {resetSuccess ? (
                <Button
                  bg="neutrals.900"
                  w="100%"
                  h="60px"
                  color="neutrals.0"
                  borderRadius="8px"
                  type="submit"
                  cursor="pointer"
                  fontSize="16px"
                  fontWeight="semibold"
                  lineHeight="20px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  onClick={() => navigate('/login')}
                  aria-label="Back to login"
                >
                  Back to login
                </Button>
              ) : (
                <Button
                  loading={isLoading}
                  loadingText="Resetting password..."
                  bg="neutrals.900"
                  w="100%"
                  h="60px"
                  color="neutrals.0"
                  borderRadius="8px"
                  type="submit"
                  cursor={isLoading ? 'not-allowed' : 'pointer'}
                  fontSize="16px"
                  fontWeight="semibold"
                  lineHeight="20px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  disabled={isLoading}
                  aria-label="Reset password"
                >
                  Reset password
                </Button>
              )}
            </Box>
          </Fieldset.Root>
        </form>
      </Flex>
    </Container>
  );
}
